import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Pagination from '../common/Pagination';
import PageHeader from '../common/PageHeader';
import PageState from '../common/PageState';
import AccountCreateModal from './AccountCreateModal';
import AccountEditModal from './AccountEditModal';
import ConfirmModal from '../common/ConfirmModal';
import { listUsers, deleteUser, UserInfoResponse } from '../../api/user';
import { formatPhoneNumber } from '../../utils/format';
import * as T from '../../styles/common/table.styled';
import { Button } from '../../styles/common/Button.styled';
import { useAuth } from '../../contexts/AuthContext';
import { isTableRowActionTarget } from '../../utils/tableInteraction';
import DebouncedSearchInput from '../common/DebouncedSearchInput';

const AccountList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { mode } = useAuth();
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const requestSequenceRef = useRef(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  // URL 파라미터에서 현재 상태 추출
  const currentPage = parseInt(searchParams.get('pg') || '1');
  const appliedSearchTerm = searchParams.get('q') || '';
  const pageSize = 10;

  // 검색 입력 필드용 로컬 상태
  const [searchTerm, setSearchTerm] = useState(appliedSearchTerm);
  const [users, setUsers] = useState<UserInfoResponse[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserInfoResponse | null>(null);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasLoadError, setHasLoadError] = useState(false);

  // 뒤로가기 등으로 URL이 변경될 때 검색 필드 동기화
  useEffect(() => {
    setSearchTerm(appliedSearchTerm);
  }, [appliedSearchTerm]);

  const fetchUsers = useCallback(async () => {
    const requestMode = mode;
    const requestSequence = ++requestSequenceRef.current;
    try {
      setIsLoading(true);
      setHasLoadError(false);
      const data = await listUsers({
        pg: currentPage,
        limit: pageSize,
        searchKeyword: appliedSearchTerm || undefined,
      });
      if (
        modeRef.current !== requestMode ||
        requestSequenceRef.current !== requestSequence
      ) return;
      setUsers(data.content);
      setTotalElements(data.totalElements);
      setTotalPages(data.totalPages);
    } catch (error) {
      if (modeRef.current !== requestMode || requestSequenceRef.current !== requestSequence) return;
      console.error('Failed to fetch users:', error);
      setHasLoadError(true);
    } finally {
      if (
        modeRef.current === requestMode &&
        requestSequenceRef.current === requestSequence
      ) setIsLoading(false);
    }
  }, [currentPage, pageSize, appliedSearchTerm, mode]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (nextSearchTerm?: string) => {
    const params: any = { pg: '1' };
    const query = typeof nextSearchTerm === 'string' ? nextSearchTerm : searchTerm;
    if (query) params.q = query;
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params: any = { pg: page.toString() };
    if (appliedSearchTerm) params.q = appliedSearchTerm;
    setSearchParams(params);
  };

  const handleConfirmDelete = async () => {
    if (!selectedUser) return;
    try {
      await deleteUser(selectedUser.userId);
      setIsConfirmOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  return (
    <T.PageContainer>
      <PageHeader title="계정 관리" count={isLoading || hasLoadError ? undefined : totalElements} actions={<Button
          onClick={() => setIsModalOpen(true)}
        >
          + 신규 계정 등록
        </Button>} />
      <T.TopBar>
        <T.SearchGroup>
          <T.SearchInput as={DebouncedSearchInput}
            aria-label="사용자명 검색"
            placeholder="사용자명 검색"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onSearch={handleSearch}
            delay={250}
          />
          <T.SearchButton onClick={() => handleSearch()}>검색</T.SearchButton>
        </T.SearchGroup>
      </T.TopBar>

      <T.TableArea $stickyActions $minWidth={950}>
        <T.Table>
          <colgroup>
            <col style={{ width: '60px' }} />
            <col style={{ width: '140px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '100px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '140px' }} />
            <col style={{ width: '160px' }} />
          </colgroup>
          <thead>
            <tr>
              <T.Th>No.</T.Th>
              <T.Th>로그인 아이디</T.Th>
              <T.Th>사용자명</T.Th>
              <T.Th>소속 팀</T.Th>
              <T.Th>역할</T.Th>
              <T.Th>내선번호</T.Th>
              <T.Th>발신번호</T.Th>
              <T.Th>관리</T.Th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <T.Tr>
                <T.Td colSpan={8}><PageState status="loading" /></T.Td>
              </T.Tr>
            ) : hasLoadError ? (
              <T.Tr><T.Td colSpan={8}><PageState status="error" title="계정 목록을 불러오지 못했습니다." onRetry={fetchUsers} /></T.Td></T.Tr>
            ) : users.length === 0 ? (
              <T.Tr>
                <T.Td colSpan={8}>
                  <PageState status="empty" title={appliedSearchTerm ? '검색 조건에 맞는 계정이 없습니다.' : '등록된 계정이 없습니다.'} />
                </T.Td>
              </T.Tr>
            ) : (
              users.map((user, index) => (
                <T.Tr
                  key={user.userId}
                  $clickable
                  onDoubleClick={(event) => {
                    if (isTableRowActionTarget(event.target)) return;
                    setSelectedUser(user);
                    setIsEditModalOpen(true);
                  }}
                >
                  <T.Td>{totalElements - (currentPage - 1) * pageSize - index}</T.Td>
                  <T.Td>{user.loginId}</T.Td>
                  <T.Td>{user.username}</T.Td>
                  <T.Td>{user.teamName || '-'}</T.Td>
                  <T.Td>{user.roleName || '-'}</T.Td>
                  <T.Td>{user.phone}</T.Td>
                  <T.Td>{formatPhoneNumber(user.dodPhone)}</T.Td>
                  <T.Td>
                    <T.ActionButton
                      style={{ marginRight: '6px' }}
                      onClick={() => { setSelectedUser(user); setIsEditModalOpen(true); }}
                    >
                      수정
                    </T.ActionButton>
                    <T.ActionButton
                      $variant="danger"
                      onClick={() => { setSelectedUser(user); setIsConfirmOpen(true); }}
                    >
                      삭제
                    </T.ActionButton>
                  </T.Td>
                </T.Tr>
              ))
            )}
          </tbody>
        </T.Table>
      </T.TableArea>

      {!isLoading && !hasLoadError && <T.TableFooter>
        <T.TotalCount aria-label="조회 결과">총 <span>{totalElements.toLocaleString()}</span>건</T.TotalCount>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </T.TableFooter>}

      {isModalOpen && (
        <AccountCreateModal
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => { setIsModalOpen(false); fetchUsers(); }}
        />
      )}
      {isEditModalOpen && (
        <AccountEditModal
          user={selectedUser}
          onClose={() => { setIsEditModalOpen(false); setSelectedUser(null); }}
          onSuccess={() => { setIsEditModalOpen(false); setSelectedUser(null); fetchUsers(); }}
        />
      )}
      {isConfirmOpen && (
        <ConfirmModal
          title="계정 삭제"
          message={`'${selectedUser?.username}(${selectedUser?.loginId})' 계정을 삭제하시겠습니까?\n삭제된 데이터는 복구할 수 없습니다.`}
          confirmLabel="삭제하기"
          cancelLabel="취소"
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsConfirmOpen(false)}
        />
      )}
    </T.PageContainer>
  );
};

export default AccountList;
